"use client";

import IconWriting from "@ui/central-icons/icons/IconWriting";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import { formatTime } from "@ui/lib/time";
import { cn } from "@ui/lib/utils";
import { iconStrokeFix } from "@ui/central-icons/iconUtils";
import { useTranslations } from "next-intl";

import { Button } from "../core/Button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../core/Tooltip";
import { AudioWidgetPill } from "./AudioWidgetPill";
import { useAudioPlayer } from "./AudioPlayerContext";

interface AudioPlayerBarProps {
  title: string;
  onEditPrompt?: () => void;
  onDownload?: () => void;
  className?: string;
}

const AudioPlayerBar = ({ title, onEditPrompt, onDownload, className }: AudioPlayerBarProps) => {
  const t = useTranslations("ClientKeys.AudioPlayerBar");
  const { playState, currentTime, duration, togglePlayPause } = useAudioPlayer();
  const isPlaying = playState === "playing";

  return (
    <AudioWidgetPill
      className={cn(
        "tw-w-full tw-max-w-[300px] md:tw-w-auto md:tw-max-w-[420px] tw-pl-[0.625rem] tw-pr-3 tw-py-2.5 tw-overflow-hidden",
        className,
      )}
    >
      {/* Play/Pause */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant='elevated'
              size='pill'
              className='tw-shrink-0 tw-shadow-[0px_0px_1px_0px_rgba(0,0,0,0.4),0px_1px_2px_0px_rgba(0,0,0,0.04),0px_2px_2px_0px_rgba(0,0,0,0.04)]'
              aria-label={isPlaying ? t("pause") : t("play")}
              icon={
                isPlaying ? (
                  <IconPauseFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                ) : (
                  <IconPlayFill className={cn(iconStrokeFix, "tw-size-4 tw-text-black")} />
                )
              }
              onClick={togglePlayPause}
            />
          </TooltipTrigger>
          <TooltipContent variant='dark-new'>{isPlaying ? t("pause") : t("play")}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Title + progress */}
      <div className='tw-flex-1 tw-min-w-0 tw-flex tw-flex-col tw-gap-1 tw-justify-center'>
        <p className='tw-type-base tw-pb-1 tw-pt-0.5 tw-text-black'>
          <span className='tw-block tw-truncate tw-whitespace-nowrap'>{title}</span>
        </p>
        <p className='tw-type-sm tw-pb-0.5 tw-tabular-nums tw-text-gray-500'>
          {formatTime(currentTime)} / {formatTime(duration)}
        </p>
      </div>

      {/* Action buttons */}
      <div className='tw-flex tw-shrink-0 tw-items-center tw-rounded-lg'>
        {onEditPrompt && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant='ghost-subtle'
                  size='pill-md'
                  className='tw-opacity-60'
                  aria-label={t("edit_prompt")}
                  icon={<IconWriting className={cn(iconStrokeFix, "tw-size-6")} />}
                  onClick={onEditPrompt}
                />
              </TooltipTrigger>
              <TooltipContent variant='dark-new'>{t("edit_prompt")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant='ghost-subtle'
                size='pill-md'
                className='tw-opacity-60'
                aria-label={t("download")}
                icon={<IconArrowInbox className={cn(iconStrokeFix, "tw-size-6")} />}
                onClick={onDownload}
              />
            </TooltipTrigger>
            <TooltipContent variant='dark-new'>{t("download")}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </AudioWidgetPill>
  );
};

AudioPlayerBar.displayName = "AudioPlayerBar";

export { AudioPlayerBar };
export type { AudioPlayerBarProps };
